import { fromJS, Map } from "immutable";
import { Action, AnyAction } from "redux";
import { combineReducers } from "redux-immutable";
import { BrevoIntegrationList } from "./types";

const BrevoActionType = {
  SETUP: "integrations/brevo/setup",
  SELECT_LIST: "integrations/brevo/selectList",
  DELETE: "integrations/brevo/delete",
  GET: "integrations/brevo/get",
  RESET_REQUESTS: "integrations/brevo/resetRequests"
} as const;
type BrevoRequestAction = Action<(typeof BrevoActionType)[keyof typeof BrevoActionType]> & {
  shopId: number;
  meta: unknown;
} & Record<string, unknown>;

export type BrevoDataReduxState = {
  settings?: {
    lists: BrevoIntegrationList[];
    selectedListId?: number;
  };
};

type BrevoDataReduxStateImmutableState = Map<unknown, unknown>;
const initialImmutableDataState = fromJS({});

type RequestErrorState = "none" | "invalid-key" | "unknown-error";

export type BrevoRequestReduxState = {
  loading: boolean;
  success: boolean;
  error: RequestErrorState;
};
type BrevoRequestReduxImmutableState = Map<keyof BrevoRequestReduxState, unknown>;

const initialRequestState: BrevoRequestReduxState = {
  loading: false,
  success: false,
  error: "none"
};
const initialImmutableRequestState = fromJS(initialRequestState);

const dataReducer = (
  state: BrevoDataReduxStateImmutableState = initialImmutableDataState,
  action: AnyAction
): BrevoDataReduxStateImmutableState => {
  switch (action.type) {
    case BrevoActionType.GET:
      return state.merge(fromJS({ settings: action.data.settings }));
    case BrevoActionType.DELETE:
      return state.merge(fromJS({ settings: undefined }));
    default:
      return state;
  }
};

export const resetBrevoIntegrationsRequestsAction = (): AnyAction => {
  return {
    type: BrevoActionType.RESET_REQUESTS
  };
};

export const getBrevoIntegrationSettingsAction = (shopId: number): BrevoRequestAction => {
  return {
    type: BrevoActionType.GET,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/brevo`,
      method: "get"
    }
  };
};
const getReducer = (
  state = initialImmutableRequestState,
  action: AnyAction
): BrevoRequestReduxImmutableState => {
  switch (action.type) {
    case BrevoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case BrevoActionType.GET:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none",
          settings: {}
        })
      );
    case `${BrevoActionType.GET}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${BrevoActionType.GET}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const deleteBrevoIntegrationAction = (shopId: number): BrevoRequestAction => {
  return {
    type: BrevoActionType.DELETE,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/brevo`,
      method: "delete",
      error: {
        400: "invalid-key"
      }
    }
  };
};
const deleteReducer = (
  state: BrevoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): BrevoRequestReduxImmutableState => {
  switch (action.type) {
    case BrevoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case BrevoActionType.DELETE:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${BrevoActionType.DELETE}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${BrevoActionType.DELETE}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const setupBrevoIntegrationAction = (shopId: number, apiKey: string): BrevoRequestAction => {
  return {
    type: BrevoActionType.SETUP,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/brevo`,
      method: "post",
      data: {
        apiKey
      },
      error: {
        400: "invalid-key"
      }
    }
  };
};
const setupReducer = (
  state: BrevoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): BrevoRequestReduxImmutableState => {
  switch (action.type) {
    case BrevoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case BrevoActionType.SETUP:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${BrevoActionType.SETUP}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${BrevoActionType.SETUP}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: action.errorMessage === "invalid-key" ? "invalid-key" : "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const selectBrevoListAction = (shopId: number, listId: number): BrevoRequestAction => {
  return {
    type: BrevoActionType.SELECT_LIST,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/brevo`,
      method: "patch",
      data: {
        listId
      }
    }
  };
};
const selectListReducer = (
  state: BrevoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): BrevoRequestReduxImmutableState => {
  switch (action.type) {
    case BrevoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case BrevoActionType.SELECT_LIST:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${BrevoActionType.SELECT_LIST}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${BrevoActionType.SELECT_LIST}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

const requestsReducer = combineReducers({
  get: getReducer,
  setup: setupReducer,
  delete: deleteReducer,
  selectList: selectListReducer
});

export const brevoIntegrationReducer = combineReducers({
  data: dataReducer,
  requests: requestsReducer
});
